/* CertificationsScreen.js — Unified Certifications Management
   ────────────────────────────────────────────────────────────────────────
   Merges the previous AddEditCertificationScreen.js + CertificationDetailsScreen.js
   into a single, fully-functional, DB-backed certifications module.

   FEATURES:
     • 6 tabs: Overview · Active · Expiring · Expired · Analytics · Compliance
     • Real CRUD against /api/data/certifications
     • Full certification form (title, issuer, holder, dates, cost, level, etc.)
     • Auto-computed status from expiry_date (valid / expiring / expired)
     • Search + 5 sort modes + 4 filter facets
     • Renewal-alert banner for certs expiring within reminder window
     • One-tap renewal — duplicates cert with future expiry
     • Bulk select + bulk renew / delete / export
     • Compliance matrix (employee × category coverage)
     • Real charts (pure RN views — bar / sparkline / category breakdown)
     • CSV + report export via Share
     • Reminder defaults (notify N days before expiry)
     • Issuer directory (NEBOSH, IOSH, CITB/CSCS, Red Cross, City & Guilds, etc.)
     • Pulse Compliance Score (composite metric)
     • dbToUi/uiToDb transformers at API boundary
     • Multi-token auth (auth_token | userToken | token)
     • Canonical company_id key, defensive || 0
     • React.memo sub-components at module scope (no typing lag)
*/

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions, Alert,
  Modal, TextInput, ActivityIndicator, RefreshControl, StatusBar, FlatList,
  Platform, Share, Animated, Easing, ScrollView, KeyboardAvoidingView, Linking,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width } = Dimensions.get('window');

// ── Brand palette ──────────────────────────────────────────────────────────
const PRIMARY    = '#0e4e96';
const PRIMARY_2  = '#1e7be2';
const SUCCESS    = '#10B981';
const WARNING    = '#F59E0B';
const DANGER     = '#EF4444';
const INFO       = '#0891B2';
const PURPLE     = '#8B5CF6';
const INDIGO     = '#6366F1';
const NEUTRAL    = '#64748B';
const BORDER     = '#E2E8F0';
const BG         = '#F8FAFC';
const SURFACE    = '#FFFFFF';

// ── API ────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY      = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getCompanyId = async () => {
  try {
    const raw = await AsyncStorage.getItem('company_id');
    if (raw) { const n = parseInt(raw); if (n > 0) return n; }
    const u = await AsyncStorage.getItem('currentUser');
    if (u) { const o = JSON.parse(u); const id = parseInt(o?.company_id || o?.companyId); if (id) return id; }
    const cp = await AsyncStorage.getItem('companyProfile');
    if (cp) { const o = JSON.parse(cp); const id = parseInt(o?.id || o?.company_id); if (id) return id; }
    return 0;
  } catch { return 0; }
};

const getAuthToken = async () => {
  try {
    return (await AsyncStorage.getItem('auth_token'))
        || (await AsyncStorage.getItem('userToken'))
        || (await AsyncStorage.getItem('token'))
        || null;
  } catch { return null; }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const cid = await getCompanyId();
    const tok = await getAuthToken();
    const [path, qs] = endpoint.split('?');
    const dbPath = path.replace(/-/g, '_');
    const baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    const params = new URLSearchParams(qs || '');
    if (cid > 0) params.set('company_id', String(cid));
    const sep = params.toString() ? '?' : '';
    const url = `${baseUrl}${sep}${params.toString()}`;
    const headers = {
      'Content-Type': 'application/json',
      'X-API-Key':    API_KEY,
      ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
    };
    const res = await fetch(url, {
      headers,
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });
    if (!res.ok) throw new Error(`API ${res.status}`);
    return await res.json();
  } catch (err) {
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.log(`[Certs] ${endpoint}:`, err?.message);
    }
    throw err;
  }
};

// ── Helpers ────────────────────────────────────────────────────────────────
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';
const fmtDateShort = (d) => d
  ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  : '';
const fmtDateISO = (d) => d ? new Date(d).toISOString().split('T')[0] : '';

const daysUntil = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
};
const relativeExpiry = (d) => {
  const days = daysUntil(d);
  if (days == null) return '';
  if (days < 0) return `expired ${Math.abs(days)}d ago`;
  if (days === 0) return 'expires today';
  if (days === 1) return 'expires tomorrow';
  if (days < 30) return `expires in ${days}d`;
  if (days < 365) return `expires in ${Math.round(days / 30)}mo`;
  return `expires in ${Math.round(days / 365)}y`;
};

const computeStatus = (cert, reminderWindow = 60) => {
  if (cert.status === 'revoked' || cert.status === 'pending') return cert.status;
  const days = daysUntil(cert.expiryDate);
  if (days == null) return cert.status || 'valid';
  if (days < 0) return 'expired';
  if (days <= reminderWindow) return 'expiring';
  return 'valid';
};

const statusMeta = (status) => {
  const map = {
    valid:    { color: SUCCESS, bg: '#D1FAE5', icon: 'check-decagram',     label: 'Valid' },
    expiring: { color: WARNING, bg: '#FEF3C7', icon: 'clock-alert-outline', label: 'Expiring' },
    expired:  { color: DANGER,  bg: '#FEE2E2', icon: 'close-circle',       label: 'Expired' },
    revoked:  { color: NEUTRAL, bg: '#F1F5F9', icon: 'cancel',             label: 'Revoked' },
    pending:  { color: INFO,    bg: '#CFFAFE', icon: 'progress-clock',     label: 'Pending' },
  };
  return map[status] || map.valid;
};

const fmtCurrency = (v, currency = 'GBP') => {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency,
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(parseFloat(v) || 0);
  } catch {
    const sym = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : '€';
    return `${sym}${(parseFloat(v) || 0).toFixed(0)}`;
  }
};

const escapeCSV = (v) => {
  if (v == null) return '';
  const s = String(v);
  if (/[,"\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
};

// ── Constants ──────────────────────────────────────────────────────────────
const CATEGORIES = ['Safety', 'Compliance', 'Technical', 'Driving', 'Trade', 'Software', 'Medical', 'Other'];
const LEVELS     = ['Foundation', 'Level 2', 'Level 3', 'Level 4', 'Level 5', 'Practitioner', 'Associate', 'Professional', 'Expert'];
const COMMON_ISSUERS = [
  'NEBOSH', 'IOSH', 'CITB', 'St John Ambulance', 'British Red Cross',
  'City & Guilds', 'HABC', 'PASMA', 'IPAF', 'AXELOS',
  'Microsoft', 'AWS', 'Google', 'Cisco', 'Oracle',
  'DVSA', 'CPCS', 'NPORS', 'NICEIC', 'Gas Safe',
];

// ── DB ↔ UI transformers ───────────────────────────────────────────────────
const dbToUi = (row) => row && ({
  id:                row.id,
  company_id:        row.company_id,
  employee_id:       row.employee_id,
  holderName:        row.holder_name || '',
  title:             row.title || '',
  issuer:            row.issuer || '',
  certificateNumber: row.certificate_number || '',
  category:          row.category || '',
  level:             row.level || '',
  issueDate:         row.issue_date,
  expiryDate:        row.expiry_date,
  status:            row.status || 'valid',
  cost:              parseFloat(row.cost) || 0,
  renewalRequired:   row.renewal_required !== false,
  reminderDays:      parseInt(row.reminder_days) || 30,
  attachmentUrl:     row.attachment_url || '',
  attachmentName:    row.attachment_name || '',
  notes:             row.notes || '',
  tags:              Array.isArray(row.tags) ? row.tags : [],
  createdAt:         row.created_at,
  updatedAt:         row.updated_at,
});

const uiToDb = (cert) => ({
  company_id:         cert.company_id,
  employee_id:        cert.employee_id || null,
  holder_name:        cert.holderName || '',
  title:              cert.title || '',
  issuer:             cert.issuer || '',
  certificate_number: cert.certificateNumber || '',
  category:           cert.category || 'Other',
  level:              cert.level || '',
  issue_date:         cert.issueDate || null,
  expiry_date:        cert.expiryDate || null,
  status:             cert.status || 'valid',
  cost:               parseFloat(cert.cost) || 0,
  renewal_required:   cert.renewalRequired !== false,
  reminder_days:      parseInt(cert.reminderDays) || 30,
  attachment_url:     cert.attachmentUrl || '',
  attachment_name:    cert.attachmentName || '',
  notes:              cert.notes || '',
  tags:               Array.isArray(cert.tags) ? cert.tags : [],
});

const emptyForm = () => ({
  id: null, company_id: 0, employee_id: null,
  holderName: '', title: '', issuer: '', certificateNumber: '',
  category: 'Safety', level: '',
  issueDate: fmtDateISO(new Date()),
  expiryDate: fmtDateISO(new Date(Date.now() + 365 * 86400000)),
  status: 'valid', cost: 0,
  renewalRequired: true, reminderDays: 30,
  attachmentUrl: '', attachmentName: '',
  notes: '', tags: [],
});

// ════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS (module scope, memoized — avoids re-mount on parent re-render)
// ════════════════════════════════════════════════════════════════════════════

const Field = React.memo(({ label, value, onChange, placeholder, keyboard, multiline, required, error }) => (
  <View style={{ marginBottom: 12 }}>
    {label ? (
      <Text style={st.formLbl}>
        {label}{required ? <Text style={{ color: DANGER }}> *</Text> : null}
      </Text>
    ) : null}
    <TextInput
      style={[st.formInput, multiline && { height: 88, textAlignVertical: 'top' },
              error && { borderColor: DANGER }]}
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      placeholderTextColor="#94A3B8"
      keyboardType={keyboard || 'default'}
      multiline={multiline}
      autoCapitalize={keyboard === 'email-address' ? 'none' : 'sentences'}
      autoCorrect={keyboard !== 'email-address'}
    />
    {error ? <Text style={st.formErr}>{error}</Text> : null}
  </View>
));

const Chip = React.memo(({ label, active, onPress, color = PRIMARY, icon }) => (
  <TouchableOpacity
    onPress={onPress}
    style={[
      { flexDirection: 'row', alignItems: 'center', gap: 4,
        paddingHorizontal: 10, paddingVertical: 6, borderRadius: 14,
        backgroundColor: '#F1F5F9', borderWidth: 1, borderColor: BORDER },
      active && { backgroundColor: color, borderColor: color },
    ]}>
    {icon ? <MaterialCommunityIcons name={icon} size={12}
              color={active ? '#FFF' : NEUTRAL} /> : null}
    <Text style={{ fontSize: 11, color: active ? '#FFF' : NEUTRAL, fontWeight: '700' }}>
      {label}
    </Text>
  </TouchableOpacity>
));

const StatusPill = React.memo(({ status, small }) => {
  const m = statusMeta(status);
  return (
    <View style={{
      flexDirection: 'row', alignItems: 'center', gap: 3,
      paddingHorizontal: small ? 6 : 8, paddingVertical: small ? 2 : 3,
      borderRadius: 8, backgroundColor: m.bg,
    }}>
      <MaterialCommunityIcons name={m.icon} size={small ? 10 : 12} color={m.color} />
      <Text style={{ fontSize: small ? 9 : 10, color: m.color, fontWeight: '800' }}>
        {m.label}
      </Text>
    </View>
  );
});

const StatCard = React.memo(({ icon, value, label, color = PRIMARY }) => (
  <View style={st.statCard}>
    <MaterialCommunityIcons name={icon} size={20} color={color} />
    <Text style={[st.statVal, { color }]}>{value}</Text>
    <Text style={st.statLbl}>{label}</Text>
  </View>
));

const BarChart = React.memo(({ data, color = PRIMARY }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value || 0), 1);
  return (
    <View style={{ gap: 8 }}>
      {data.map((d, i) => (
        <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Text style={{ width: 80, fontSize: 11, color: '#475569', fontWeight: '600' }} numberOfLines={1}>
            {d.label}
          </Text>
          <View style={{ flex: 1, height: 18, backgroundColor: '#F1F5F9', borderRadius: 4, overflow: 'hidden' }}>
            <View style={{
              width: `${(d.value / max) * 100}%`,
              height: '100%',
              backgroundColor: d.color || color,
              borderRadius: 4, justifyContent: 'center', paddingHorizontal: 6,
            }}>
              {d.value / max > 0.22 && (
                <Text style={{ fontSize: 10, color: '#FFF', fontWeight: '700' }}>
                  {d.displayValue || d.value}
                </Text>
              )}
            </View>
          </View>
          {d.value / max <= 0.22 && (
            <Text style={{ fontSize: 11, color: NEUTRAL, fontWeight: '700' }}>
              {d.displayValue || d.value}
            </Text>
          )}
        </View>
      ))}
    </View>
  );
});

const Sparkline = React.memo(({ data, color = PRIMARY, height: h = 36, width: w = 90 }) => {
  if (!data || data.length === 0) return <View style={{ height: h, width: w }} />;
  const max = Math.max(...data, 1);
  const bw = Math.max(2, (w - (data.length - 1) * 2) / data.length);
  return (
    <View style={{ height: h, width: w, flexDirection: 'row', alignItems: 'flex-end' }}>
      {data.map((v, i) => (
        <View key={i} style={{
          width: bw, marginRight: i === data.length - 1 ? 0 : 2,
          height: Math.max(2, (v / max) * h),
          backgroundColor: color, borderRadius: 1,
          opacity: 0.5 + 0.5 * (i / data.length),
        }} />
      ))}
    </View>
  );
});

const Selector = React.memo(({ label, value, options, onChange, required }) => {
  const [open, setOpen] = useState(false);
  return (
    <View style={{ marginBottom: 12 }}>
      {label ? (
        <Text style={st.formLbl}>
          {label}{required ? <Text style={{ color: DANGER }}> *</Text> : null}
        </Text>
      ) : null}
      <TouchableOpacity onPress={() => setOpen(o => !o)}
        style={[st.formInput, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
        <Text style={{ color: value ? '#0F172A' : '#94A3B8', fontSize: 14 }}>
          {value || `Select ${label?.toLowerCase()}...`}
        </Text>
        <MaterialCommunityIcons name={open ? 'chevron-up' : 'chevron-down'} size={20} color={NEUTRAL} />
      </TouchableOpacity>
      {open ? (
        <View style={st.selectorDropdown}>
          <ScrollView style={{ maxHeight: 220 }} keyboardShouldPersistTaps="handled">
            {options.map(opt => {
              const v = typeof opt === 'string' ? opt : opt.value;
              const l = typeof opt === 'string' ? opt : opt.label;
              return (
                <TouchableOpacity key={v}
                  style={[st.selectorOpt, value === v && { backgroundColor: '#EFF6FF' }]}
                  onPress={() => { onChange(v); setOpen(false); }}>
                  <Text style={{ fontSize: 14, color: value === v ? PRIMARY : '#1E293B',
                                 fontWeight: value === v ? '700' : '500' }}>{l}</Text>
                  {value === v ? (
                    <MaterialCommunityIcons name="check" size={18} color={PRIMARY} />
                  ) : null}
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
});

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function CertificationsScreen({ navigation, route }) {
  // ── State ─────────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isSaving, setIsSaving]     = useState(false);

  const [activeTab, setActiveTab] = useState('overview');
  const [certs, setCerts]         = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companyId, setCompanyId] = useState(0);
  const [companyName, setCompanyName] = useState('');

  // Search / sort / filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('expiry_asc');
  const [filterOptions, setFilterOptions] = useState({
    status: 'all', category: 'all', issuer: 'all', employee: 'all', renewal: 'all',
  });
  const [expiringWindow, setExpiringWindow] = useState(60); // days

  // Modals
  const [showFormModal, setShowFormModal]     = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});

  // Bulk select
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // FAB
  const [fabOpen, setFabOpen] = useState(false);
  const fabAnim = useRef(new Animated.Value(0)).current;

  // Settings
  const [settings, setSettings] = useState({
    defaultReminderDays: 30,
    autoComputeStatus:   true,
  });

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => { initialize(); }, []);

  const initialize = useCallback(async () => {
    try {
      setIsLoading(true);
      const cid = await getCompanyId();
      setCompanyId(cid);
      try {
        const cp = await AsyncStorage.getItem('companyProfile');
        if (cp) {
          const c = JSON.parse(cp);
          setCompanyName(c?.name || c?.company || '');
        }
      } catch {}

      // Persisted settings
      try {
        const s = await AsyncStorage.getItem('certs_settings');
        if (s) setSettings(prev => ({ ...prev, ...JSON.parse(s) }));
      } catch {}

      // Load employees (best-effort) and certifications
      const [empResp, certResp] = await Promise.allSettled([
        apiRequest('employees'),
        apiRequest('certifications'),
      ]);

      // Employees
      if (empResp.status === 'fulfilled' && Array.isArray(empResp.value?.data)) {
        setEmployees(empResp.value.data);
      } else {
        // try cached employees
        try {
          const cached = await AsyncStorage.getItem('employees_cache');
          if (cached) setEmployees(JSON.parse(cached));
        } catch {}
      }

      // Certifications
      let loaded = null;
      if (certResp.status === 'fulfilled' && Array.isArray(certResp.value?.data)) {
        loaded = certResp.value.data.map(dbToUi).filter(Boolean);
        await AsyncStorage.setItem('certifications_cache', JSON.stringify(loaded));
      } else {
        try {
          const cached = await AsyncStorage.getItem('certifications_cache');
          if (cached) loaded = JSON.parse(cached);
        } catch {}
      }

      // Auto-compute status from expiry if enabled
      if (loaded) {
        if (settings.autoComputeStatus) {
          loaded = loaded.map(c => ({ ...c, status: computeStatus(c, c.reminderDays || 30) }));
        }
        setCerts(loaded);
      } else {
        setCerts([]);
      }
    } catch (err) {
      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        console.log('[Certs] init:', err?.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [settings.autoComputeStatus]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await initialize();
    setRefreshing(false);
  }, [initialize]);

  // ── Persistence ───────────────────────────────────────────────────────────
  const persistLocalOnly = useCallback(async (next) => {
    setCerts(next);
    try { await AsyncStorage.setItem('certifications_cache', JSON.stringify(next)); } catch {}
  }, []);

  const saveCertRow = useCallback(async (cert) => {
    const dbRow = uiToDb({ ...cert, company_id: cert.company_id || companyId });
    try {
      if (cert.id && Number.isInteger(cert.id) && cert.id > 0 && cert.id < 1e9) {
        await apiRequest(`certifications/${cert.id}`, { method: 'PUT', body: dbRow });
        return cert;
      }
      const resp = await apiRequest('certifications', { method: 'POST', body: dbRow });
      if (resp?.success && resp.data?.id) return { ...cert, id: resp.data.id };
      return cert;
    } catch (err) {
      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        console.log('[Certs] save:', err?.message);
      }
      return cert;
    }
  }, [companyId]);

  const deleteCertRow = useCallback(async (id) => {
    try { await apiRequest(`certifications/${id}`, { method: 'DELETE' }); }
    catch {}
  }, []);

  const persistSettings = useCallback(async (next) => {
    setSettings(next);
    try { await AsyncStorage.setItem('certs_settings', JSON.stringify(next)); } catch {}
  }, []);

  // ── Computed analytics ────────────────────────────────────────────────────
  const overview = useMemo(() => {
    const total = certs.length;
    if (total === 0) {
      return { total: 0, valid: 0, expiring: 0, expired: 0, totalCost: 0,
               byCategory: {}, byIssuer: {}, pulseScore: 0,
               nextExpiry: null, costByCategory: {} };
    }
    const valid    = certs.filter(c => c.status === 'valid').length;
    const expiring = certs.filter(c => c.status === 'expiring').length;
    const expired  = certs.filter(c => c.status === 'expired').length;
    const totalCost = certs.reduce((s, c) => s + (parseFloat(c.cost) || 0), 0);

    const byCategory = {};
    const byIssuer = {};
    const costByCategory = {};
    certs.forEach(c => {
      const cat = c.category || 'Other';
      const iss = c.issuer || 'Unknown';
      byCategory[cat] = (byCategory[cat] || 0) + 1;
      byIssuer[iss] = (byIssuer[iss] || 0) + 1;
      costByCategory[cat] = (costByCategory[cat] || 0) + (parseFloat(c.cost) || 0);
    });

    const futureExpiries = certs
      .filter(c => c.expiryDate && c.status !== 'expired')
      .sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate));
    const nextExpiry = futureExpiries[0] || null;

    // Pulse Compliance Score: composite 0-100
    const validShare    = total ? (valid / total) * 100 : 0;
    const expiringPenalty = total ? (expiring / total) * 30 : 0;
    const expiredPenalty  = total ? (expired / total) * 100 : 0;
    const pulseScore = Math.max(0, Math.round(validShare - expiringPenalty - expiredPenalty + 30));

    return { total, valid, expiring, expired, totalCost,
             byCategory, byIssuer, costByCategory, pulseScore, nextExpiry };
  }, [certs]);

  const monthlyIssuance = useMemo(() => {
    const buckets = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const next = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      const count = certs.filter(c => {
        if (!c.issueDate) return false;
        const id = new Date(c.issueDate);
        return id >= d && id < next;
      }).length;
      buckets.push({
        month: d.toLocaleDateString('en-GB', { month: 'short' }),
        value: count,
      });
    }
    return buckets;
  }, [certs]);

  const expiringInWindow = useMemo(() => {
    return certs
      .filter(c => {
        const days = daysUntil(c.expiryDate);
        return days != null && days >= 0 && days <= expiringWindow;
      })
      .sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate));
  }, [certs, expiringWindow]);

  // Compliance matrix: each employee × category
  const complianceMatrix = useMemo(() => {
    const empNames = Array.from(new Set([
      ...employees.map(e => `${e.first_name || ''} ${e.last_name || ''}`.trim()).filter(Boolean),
      ...certs.map(c => c.holderName).filter(Boolean),
    ])).slice(0, 30);
    const matrix = empNames.map(name => {
      const row = { name, categories: {} };
      CATEGORIES.forEach(cat => {
        const mine = certs.filter(c => c.holderName === name && c.category === cat);
        if (mine.length === 0) {
          row.categories[cat] = { state: 'missing', count: 0 };
        } else {
          const worst = mine.reduce((acc, c) => {
            const order = { expired: 3, expiring: 2, valid: 1 };
            return order[c.status] > order[acc.status] ? c : acc;
          }, mine[0]);
          row.categories[cat] = { state: worst.status, count: mine.length };
        }
      });
      return row;
    });
    return matrix;
  }, [certs, employees]);

  // ── Filter + sort ─────────────────────────────────────────────────────────
  const filteredCerts = useMemo(() => {
    let list = certs;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(c =>
        (c.title || '').toLowerCase().includes(q) ||
        (c.holderName || '').toLowerCase().includes(q) ||
        (c.issuer || '').toLowerCase().includes(q) ||
        (c.certificateNumber || '').toLowerCase().includes(q) ||
        (c.category || '').toLowerCase().includes(q) ||
        (c.notes || '').toLowerCase().includes(q) ||
        (c.tags || []).some(t => String(t).toLowerCase().includes(q))
      );
    }
    if (filterOptions.status !== 'all')   list = list.filter(c => c.status === filterOptions.status);
    if (filterOptions.category !== 'all') list = list.filter(c => c.category === filterOptions.category);
    if (filterOptions.issuer !== 'all')   list = list.filter(c => c.issuer === filterOptions.issuer);
    if (filterOptions.employee !== 'all') list = list.filter(c => c.holderName === filterOptions.employee);
    if (filterOptions.renewal === 'required')    list = list.filter(c => c.renewalRequired);
    if (filterOptions.renewal === 'one_off')     list = list.filter(c => !c.renewalRequired);

    // Tab-specific filter
    if (activeTab === 'active')   list = list.filter(c => c.status === 'valid');
    if (activeTab === 'expiring') list = list.filter(c => c.status === 'expiring');
    if (activeTab === 'expired')  list = list.filter(c => c.status === 'expired');

    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'expiry_asc':  return (new Date(a.expiryDate || '9999')) - (new Date(b.expiryDate || '9999'));
        case 'expiry_desc': return (new Date(b.expiryDate || '0')) - (new Date(a.expiryDate || '0'));
        case 'title_asc':   return (a.title || '').localeCompare(b.title || '');
        case 'holder_asc':  return (a.holderName || '').localeCompare(b.holderName || '');
        case 'cost_desc':   return (parseFloat(b.cost) || 0) - (parseFloat(a.cost) || 0);
        default:            return 0;
      }
    });
    return list;
  }, [certs, searchQuery, filterOptions, sortBy, activeTab]);

  // ── Form helpers ──────────────────────────────────────────────────────────
  const openAddForm = useCallback(() => {
    setFormData({ ...emptyForm(), company_id: companyId,
                  reminderDays: settings.defaultReminderDays });
    setFormErrors({});
    setSelectedCert(null);
    setShowFormModal(true);
  }, [companyId, settings.defaultReminderDays]);

  const openEditForm = useCallback((cert) => {
    setFormData({ ...cert });
    setFormErrors({});
    setSelectedCert(cert);
    setShowFormModal(true);
  }, []);

  const updateFormField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
    }
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errs = {};
    if (!formData.title?.trim()) errs.title = 'Title is required';
    if (!formData.holderName?.trim()) errs.holderName = 'Holder name is required';
    if (!formData.issuer?.trim()) errs.issuer = 'Issuer is required';
    if (formData.issueDate && formData.expiryDate) {
      if (new Date(formData.expiryDate) <= new Date(formData.issueDate)) {
        errs.expiryDate = 'Must be after issue date';
      }
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData]);

  const submitForm = useCallback(async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        company_id: companyId,
        status: settings.autoComputeStatus
          ? computeStatus(formData, formData.reminderDays || 30)
          : formData.status,
      };
      const isEdit = !!formData.id;
      let next;
      if (isEdit) {
        next = certs.map(c => c.id === formData.id ? payload : c);
      } else {
        const tempId = `tmp_${Date.now()}`;
        next = [{ ...payload, id: tempId }, ...certs];
      }
      await persistLocalOnly(next);
      const saved = await saveCertRow(payload);
      if (!isEdit && saved.id !== payload.id) {
        const final = next.map(c => c.id === payload.id || typeof c.id === 'string'
                                ? saved : c);
        await persistLocalOnly(final);
      }
      setShowFormModal(false);
      Alert.alert('Saved', isEdit ? 'Certification updated.' : 'Certification added.');
    } catch (err) {
      Alert.alert('Save Failed', err?.message || 'Could not save certification.');
    } finally {
      setIsSaving(false);
    }
  }, [formData, certs, persistLocalOnly, saveCertRow, validateForm, companyId, settings.autoComputeStatus]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const deleteCert = useCallback((cert) => {
    Alert.alert(
      'Delete Certification',
      `Delete "${cert.title}" for ${cert.holderName}? This cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: async () => {
          const next = certs.filter(c => c.id !== cert.id);
          await persistLocalOnly(next);
          await deleteCertRow(cert.id);
          setShowDetailModal(false);
        }},
      ]
    );
  }, [certs, persistLocalOnly, deleteCertRow]);

  const renewCert = useCallback((cert) => {
    // Create a new cert with a renewed expiry (1 year default), copying everything else
    const newCert = {
      ...cert,
      id: null,
      issueDate: fmtDateISO(new Date()),
      expiryDate: fmtDateISO(new Date(Date.now() + 365 * 86400000)),
      status: 'valid',
      notes: cert.notes
        ? `${cert.notes}\n\nRenewed from cert #${cert.id} on ${fmtDate(new Date())}`
        : `Renewed from cert #${cert.id} on ${fmtDate(new Date())}`,
    };
    setFormData(newCert);
    setFormErrors({});
    setSelectedCert(null);
    setShowDetailModal(false);
    setShowFormModal(true);
  }, []);

  // Bulk actions
  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectMode(false);
  }, []);

  const bulkDelete = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    Alert.alert(
      'Delete Selected',
      `Delete ${ids.length} certification${ids.length === 1 ? '' : 's'}? This cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete All', style: 'destructive', onPress: async () => {
          const next = certs.filter(c => !ids.includes(c.id));
          await persistLocalOnly(next);
          for (const id of ids) await deleteCertRow(id);
          clearSelection();
        }},
      ]
    );
  }, [selectedIds, certs, persistLocalOnly, deleteCertRow, clearSelection]);

  // Exports
  const exportCSV = useCallback(async () => {
    if (certs.length === 0) { Alert.alert('No data', 'Nothing to export.'); return; }
    const headers = ['ID', 'Title', 'Holder', 'Issuer', 'Number', 'Category', 'Level',
                     'Issue Date', 'Expiry Date', 'Status', 'Cost', 'Renewal', 'Notes'];
    const rows = certs.map(c => [
      c.id, c.title, c.holderName, c.issuer, c.certificateNumber,
      c.category, c.level, c.issueDate, c.expiryDate, c.status,
      c.cost, c.renewalRequired ? 'yes' : 'no', c.notes,
    ]);
    const csv = [headers, ...rows].map(r => r.map(escapeCSV).join(',')).join('\n');
    try {
      await Share.share({
        message: csv,
        title: `${companyName || 'Company'} — Certifications (${certs.length})`,
      });
    } catch (e) {
      Alert.alert('Export Failed', e?.message || 'Could not share.');
    }
  }, [certs, companyName]);

  const exportComplianceReport = useCallback(async () => {
    const lines = [
      `═══ ${(companyName || 'COMPANY').toUpperCase()} — COMPLIANCE REPORT ═══`,
      `Generated: ${new Date().toLocaleString('en-GB')}`,
      '',
      `Pulse Compliance Score: ${overview.pulseScore} / 100`,
      `Total Certifications:   ${overview.total}`,
      `Valid:                  ${overview.valid}`,
      `Expiring (≤${expiringWindow}d):       ${overview.expiring}`,
      `Expired:                ${overview.expired}`,
      `Total Spend:            ${fmtCurrency(overview.totalCost)}`,
      '',
      `── EXPIRING SOON (${expiringInWindow.length}) ──`,
      ...expiringInWindow.slice(0, 20).map(c =>
        `• ${c.title}\n  ${c.holderName} · ${c.issuer} · ${relativeExpiry(c.expiryDate)}`
      ),
      '',
      `── EXPIRED (${overview.expired}) ──`,
      ...certs.filter(c => c.status === 'expired').slice(0, 20).map(c =>
        `• ${c.title} (${c.holderName}) — ${relativeExpiry(c.expiryDate)}`
      ),
      '',
      `── BY CATEGORY ──`,
      ...Object.entries(overview.byCategory)
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => `${k.padEnd(15)} ${v}`),
    ];
    try {
      await Share.share({
        message: lines.join('\n'),
        title: `${companyName} — Compliance Report`,
      });
    } catch (e) {
      Alert.alert('Export Failed', e?.message || 'Could not share.');
    }
  }, [companyName, overview, certs, expiringInWindow, expiringWindow]);

  // FAB
  const toggleFAB = useCallback(() => {
    const next = !fabOpen;
    Animated.timing(fabAnim, {
      toValue: next ? 1 : 0, duration: 200,
      easing: Easing.out(Easing.quad), useNativeDriver: true,
    }).start();
    setFabOpen(next);
  }, [fabOpen, fabAnim]);

  // ── Render: tab bar ───────────────────────────────────────────────────────
  const TABS = [
    { k: 'overview',   l: 'Overview',   i: 'view-dashboard',          n: 0 },
    { k: 'active',     l: 'Active',     i: 'check-decagram',          n: overview.valid },
    { k: 'expiring',   l: 'Expiring',   i: 'clock-alert-outline',     n: overview.expiring },
    { k: 'expired',    l: 'Expired',    i: 'close-circle',            n: overview.expired },
    { k: 'analytics',  l: 'Analytics',  i: 'chart-bar',               n: 0 },
    { k: 'compliance', l: 'Compliance', i: 'shield-check',            n: 0 },
  ];

  const renderTabBar = () => (
    <View style={st.tabBar}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 4 }}>
        {TABS.map(t => (
          <TouchableOpacity key={t.k}
            style={[st.tabBtn, activeTab === t.k && st.tabBtnActive]}
            onPress={() => setActiveTab(t.k)}>
            <MaterialCommunityIcons name={t.i} size={16}
              color={activeTab === t.k ? PRIMARY : '#94A3B8'} />
            <Text style={[st.tabTxt, activeTab === t.k && { color: PRIMARY, fontWeight: '700' }]}>
              {t.l}
            </Text>
            {t.n > 0 ? (
              <View style={{
                backgroundColor: t.k === 'expiring' ? WARNING : t.k === 'expired' ? DANGER : PRIMARY + '20',
                borderRadius: 8, paddingHorizontal: 5, paddingVertical: 1, minWidth: 18, alignItems: 'center',
              }}>
                <Text style={{ fontSize: 10, fontWeight: '800',
                  color: t.k === 'expiring' || t.k === 'expired' ? '#FFF' : PRIMARY }}>{t.n}</Text>
              </View>
            ) : null}
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // ── Render: cert card ─────────────────────────────────────────────────────
  const renderCertCard = (cert) => {
    const m = statusMeta(cert.status);
    const isSelected = selectedIds.has(cert.id);
    const days = daysUntil(cert.expiryDate);
    return (
      <TouchableOpacity activeOpacity={0.8}
        style={[st.certCard, isSelected && { borderColor: PRIMARY, borderWidth: 2 }]}
        onPress={() => {
          if (selectMode) toggleSelect(cert.id);
          else { setSelectedCert(cert); setShowDetailModal(true); }
        }}
        onLongPress={() => {
          if (!selectMode) {
            setSelectMode(true);
            toggleSelect(cert.id);
          }
        }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
          {selectMode ? (
            <View style={[st.checkbox, isSelected && { backgroundColor: PRIMARY, borderColor: PRIMARY }]}>
              {isSelected ? <MaterialCommunityIcons name="check" size={14} color="#FFF" /> : null}
            </View>
          ) : (
            <View style={[st.certIcon, { backgroundColor: m.bg }]}>
              <MaterialCommunityIcons name="certificate" size={20} color={m.color} />
            </View>
          )}
          <View style={{ flex: 1 }}>
            <Text style={st.certTitle} numberOfLines={2}>{cert.title}</Text>
            <Text style={st.certHolder}>{cert.holderName || '—'}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
              <StatusPill status={cert.status} small />
              {cert.category ? (
                <Text style={st.certMeta}>{cert.category}</Text>
              ) : null}
              {cert.issuer ? (
                <>
                  <Text style={st.certMetaDot}>·</Text>
                  <Text style={st.certMeta} numberOfLines={1}>{cert.issuer}</Text>
                </>
              ) : null}
            </View>
            {cert.expiryDate ? (
              <Text style={[st.certExpiry, { color: m.color }]}>
                {relativeExpiry(cert.expiryDate)} · {fmtDate(cert.expiryDate)}
              </Text>
            ) : null}
          </View>
          {cert.cost > 0 ? (
            <Text style={st.certCost}>{fmtCurrency(cert.cost)}</Text>
          ) : null}
        </View>
      </TouchableOpacity>
    );
  };

  // ── Render: unified flat-list data feed ───────────────────────────────────
  const flatListData = useMemo(() => {
    const data = [];
    if (activeTab === 'overview') {
      data.push({ id: 'pulse',    type: 'pulse' });
      data.push({ id: 'stats',    type: 'stats-grid' });
      if (expiringInWindow.length > 0) {
        data.push({ id: 'expiring-banner', type: 'expiring-banner' });
        expiringInWindow.slice(0, 5).forEach(c => data.push({ id: `eb-${c.id}`, type: 'cert', data: c }));
      }
      data.push({ id: 'recent-h', type: 'recent-header' });
      certs.slice(0, 3).forEach(c => data.push({ id: `r-${c.id}`, type: 'cert', data: c }));
    } else if (activeTab === 'active' || activeTab === 'expiring' || activeTab === 'expired') {
      data.push({ id: 'search', type: 'search' });
      data.push({ id: 'list-h', type: 'list-header' });
      if (filteredCerts.length === 0) {
        data.push({ id: 'empty', type: 'empty', text: 'No certifications match your filters.', icon: 'certificate-outline' });
      } else {
        filteredCerts.forEach(c => data.push({ id: `c-${c.id}`, type: 'cert', data: c }));
      }
    } else if (activeTab === 'analytics') {
      data.push({ id: 'a-cat',     type: 'analytics-categories' });
      data.push({ id: 'a-status',  type: 'analytics-status' });
      data.push({ id: 'a-issuance', type: 'analytics-issuance' });
      data.push({ id: 'a-issuer',  type: 'analytics-issuer' });
      data.push({ id: 'a-cost',    type: 'analytics-cost' });
    } else if (activeTab === 'compliance') {
      data.push({ id: 'matrix-h', type: 'matrix-header' });
      data.push({ id: 'matrix',   type: 'matrix' });
      data.push({ id: 'settings-h', type: 'settings-header' });
      data.push({ id: 'settings-reminder', type: 'setting-reminder' });
      data.push({ id: 'settings-auto',     type: 'setting-auto' });
      data.push({ id: 'settings-csv',      type: 'setting-action', icon: 'download', title: 'Export CSV',
                  desc: 'All certifications as spreadsheet', action: exportCSV });
      data.push({ id: 'settings-report',   type: 'setting-action', icon: 'file-document-outline', title: 'Compliance Report',
                  desc: 'Plain-text summary for sharing', action: exportComplianceReport });
    }
    return data;
  }, [activeTab, certs, filteredCerts, expiringInWindow, exportCSV, exportComplianceReport]);

  // ── Item renderer ─────────────────────────────────────────────────────────
  const renderItem = ({ item }) => {
    switch (item.type) {
      case 'pulse':
        return (
          <View style={st.pulseCard}>
            <View>
              <Text style={st.pulseLbl}>PULSE COMPLIANCE SCORE</Text>
              <Text style={[st.pulseVal, {
                color: overview.pulseScore >= 80 ? SUCCESS
                     : overview.pulseScore >= 60 ? PRIMARY
                     : overview.pulseScore >= 40 ? WARNING : DANGER,
              }]}>{overview.pulseScore}<Text style={{ fontSize: 16, color: NEUTRAL }}>/100</Text></Text>
              <Text style={st.pulseDesc} numberOfLines={2}>
                {overview.pulseScore >= 80 ? 'Excellent — team is well-certified'
                 : overview.pulseScore >= 60 ? 'Good — a few renewals coming up'
                 : overview.pulseScore >= 40 ? 'Action needed — multiple expiring' : 'Critical — expired certs require urgent attention'}
              </Text>
            </View>
            <Sparkline data={monthlyIssuance.map(m => m.value)} color={PRIMARY} height={50} width={86} />
          </View>
        );

      case 'stats-grid':
        return (
          <View style={st.statsGrid}>
            <StatCard icon="certificate"     value={overview.total}    label="Total"    color={PRIMARY} />
            <StatCard icon="check-decagram"  value={overview.valid}    label="Valid"    color={SUCCESS} />
            <StatCard icon="clock-alert-outline" value={overview.expiring} label="Expiring" color={WARNING} />
            <StatCard icon="close-circle"    value={overview.expired}  label="Expired"  color={DANGER}  />
            <StatCard icon="cash"            value={fmtCurrency(overview.totalCost)} label="Total Spend" color={PURPLE} />
            <StatCard icon="account-group"   value={Object.keys(certs.reduce((a,c) => ({ ...a, [c.holderName]: 1 }), {})).length} label="Holders" color={INDIGO} />
          </View>
        );

      case 'expiring-banner':
        return (
          <View style={[st.sectionCard, { backgroundColor: '#FEF3C7', borderColor: WARNING }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <MaterialCommunityIcons name="alert-circle" size={22} color={WARNING} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: '800', color: '#92400E' }}>
                  {expiringInWindow.length} cert{expiringInWindow.length === 1 ? '' : 's'} expiring within {expiringWindow} days
                </Text>
                <Text style={{ fontSize: 11, color: '#92400E', marginTop: 2 }}>
                  Tap to view; long-press a card to bulk-renew
                </Text>
              </View>
            </View>
          </View>
        );

      case 'recent-header':
        return (
          <View style={st.subHead}>
            <Text style={st.sectionTitle}>Recent Certifications</Text>
            <TouchableOpacity onPress={() => setActiveTab('active')}>
              <Text style={{ fontSize: 12, color: PRIMARY, fontWeight: '700' }}>See all →</Text>
            </TouchableOpacity>
          </View>
        );

      case 'search':
        return (
          <View style={{ paddingHorizontal: 16, paddingTop: 12 }}>
            <View style={st.searchRow}>
              <MaterialCommunityIcons name="magnify" size={18} color={NEUTRAL} />
              <TextInput
                style={st.searchInput}
                placeholder="Search certifications..."
                placeholderTextColor="#94A3B8"
                value={searchQuery}
                onChangeText={setSearchQuery} />
              {searchQuery ? (
                <TouchableOpacity onPress={() => setSearchQuery('')}>
                  <MaterialCommunityIcons name="close-circle" size={16} color="#94A3B8" />
                </TouchableOpacity>
              ) : null}
              <View style={{ width: 1, height: 18, backgroundColor: BORDER, marginHorizontal: 4 }} />
              <TouchableOpacity onPress={() => {
                const orders = ['expiry_asc', 'expiry_desc', 'title_asc', 'holder_asc', 'cost_desc'];
                const idx = orders.indexOf(sortBy);
                setSortBy(orders[(idx + 1) % orders.length]);
              }}>
                <MaterialCommunityIcons name="sort" size={18} color={PRIMARY} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setShowFilterModal(true)}>
                <MaterialCommunityIcons name="filter-variant" size={18} color={PRIMARY} />
              </TouchableOpacity>
            </View>
            <Text style={{ fontSize: 10, color: NEUTRAL, marginTop: 4, fontStyle: 'italic' }}>
              Sorted by: {sortBy.replace(/_/g, ' ')}
            </Text>
          </View>
        );

      case 'list-header': {
        const allFilters = Object.values(filterOptions).some(v => v !== 'all');
        return (
          <View style={[st.subHead, { paddingTop: 8 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A' }}>
                {filteredCerts.length} of {certs.length}
              </Text>
              {selectMode ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Text style={{ fontSize: 12, color: PRIMARY, fontWeight: '700' }}>
                    {selectedIds.size} selected
                  </Text>
                  {selectedIds.size > 0 ? (
                    <TouchableOpacity onPress={bulkDelete} style={st.bulkBtn}>
                      <MaterialCommunityIcons name="delete" size={14} color={DANGER} />
                      <Text style={{ fontSize: 11, color: DANGER, fontWeight: '700' }}>Delete</Text>
                    </TouchableOpacity>
                  ) : null}
                  <TouchableOpacity onPress={clearSelection}>
                    <MaterialCommunityIcons name="close" size={18} color={NEUTRAL} />
                  </TouchableOpacity>
                </View>
              ) : null}
            </View>
            {!selectMode && (searchQuery || allFilters) ? (
              <TouchableOpacity onPress={() => {
                setSearchQuery('');
                setFilterOptions({ status: 'all', category: 'all', issuer: 'all', employee: 'all', renewal: 'all' });
              }} style={st.clearBtn}>
                <Text style={{ fontSize: 11, color: DANGER, fontWeight: '700' }}>Clear</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        );
      }

      case 'cert':
        return renderCertCard(item.data);

      case 'empty':
        return (
          <View style={st.emptyBox}>
            <MaterialCommunityIcons name={item.icon || 'inbox-outline'} size={48} color="#CBD5E1" />
            <Text style={st.emptyTxt}>{item.text}</Text>
            <TouchableOpacity style={st.addBtn} onPress={openAddForm}>
              <MaterialCommunityIcons name="plus" size={16} color="#FFF" />
              <Text style={{ color: '#FFF', fontWeight: '700' }}>Add First Certification</Text>
            </TouchableOpacity>
          </View>
        );

      case 'analytics-categories': {
        const data = Object.entries(overview.byCategory)
          .map(([label, value]) => ({ label, value }))
          .sort((a, b) => b.value - a.value);
        if (data.length === 0) return null;
        return (
          <View style={st.sectionCard}>
            <Text style={st.sectionTitle}>By Category</Text>
            <View style={{ marginTop: 12 }}>
              <BarChart data={data} color={PRIMARY} />
            </View>
          </View>
        );
      }

      case 'analytics-status':
        return (
          <View style={st.sectionCard}>
            <Text style={st.sectionTitle}>Status Breakdown</Text>
            <View style={{ flexDirection: 'row', gap: 6, marginTop: 12 }}>
              {[
                { l: 'Valid',    n: overview.valid,    c: SUCCESS },
                { l: 'Expiring', n: overview.expiring, c: WARNING },
                { l: 'Expired',  n: overview.expired,  c: DANGER  },
              ].map(s => (
                <View key={s.l} style={[st.statusBox, { borderLeftColor: s.c }]}>
                  <Text style={[st.statusBoxNum, { color: s.c }]}>{s.n}</Text>
                  <Text style={st.statusBoxLbl}>{s.l}</Text>
                </View>
              ))}
            </View>
          </View>
        );

      case 'analytics-issuance':
        return (
          <View style={st.sectionCard}>
            <Text style={st.sectionTitle}>Monthly Issuance</Text>
            <Text style={st.sectionSub}>Last 6 months</Text>
            <View style={{ marginTop: 12 }}>
              <BarChart data={monthlyIssuance.map(m => ({ label: m.month, value: m.value }))} color={INDIGO} />
            </View>
          </View>
        );

      case 'analytics-issuer': {
        const data = Object.entries(overview.byIssuer)
          .map(([label, value]) => ({ label, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 8);
        if (data.length === 0) return null;
        return (
          <View style={st.sectionCard}>
            <Text style={st.sectionTitle}>By Issuer</Text>
            <View style={{ marginTop: 12 }}>
              <BarChart data={data} color={PURPLE} />
            </View>
          </View>
        );
      }

      case 'analytics-cost': {
        const data = Object.entries(overview.costByCategory)
          .map(([label, value]) => ({ label, value, displayValue: fmtCurrency(value) }))
          .sort((a, b) => b.value - a.value);
        if (data.length === 0) return null;
        return (
          <View style={st.sectionCard}>
            <Text style={st.sectionTitle}>Spend by Category</Text>
            <Text style={st.sectionSub}>Total: {fmtCurrency(overview.totalCost)}</Text>
            <View style={{ marginTop: 12 }}>
              <BarChart data={data} color={WARNING} />
            </View>
          </View>
        );
      }

      case 'matrix-header':
        return (
          <View style={[st.subHead, { paddingTop: 12 }]}>
            <Text style={st.sectionTitle}>Compliance Matrix</Text>
            <Text style={{ fontSize: 11, color: NEUTRAL }}>{complianceMatrix.length} people</Text>
          </View>
        );

      case 'matrix':
        return (
          <View style={st.sectionCard}>
            <ScrollView horizontal showsHorizontalScrollIndicator>
              <View>
                <View style={{ flexDirection: 'row' }}>
                  <View style={[st.matrixCell, st.matrixHeaderCell, { width: 130 }]}>
                    <Text style={st.matrixHeaderTxt}>Holder</Text>
                  </View>
                  {CATEGORIES.map(cat => (
                    <View key={cat} style={[st.matrixCell, st.matrixHeaderCell, { width: 70 }]}>
                      <Text style={st.matrixHeaderTxt} numberOfLines={1}>{cat}</Text>
                    </View>
                  ))}
                </View>
                {complianceMatrix.length === 0 ? (
                  <View style={st.emptyBox}>
                    <MaterialCommunityIcons name="account-question" size={36} color="#CBD5E1" />
                    <Text style={st.emptyTxt}>No holders yet. Add certifications to populate.</Text>
                  </View>
                ) : complianceMatrix.map(row => (
                  <View key={row.name} style={{ flexDirection: 'row' }}>
                    <View style={[st.matrixCell, { width: 130, alignItems: 'flex-start' }]}>
                      <Text style={{ fontSize: 11, color: '#0F172A', fontWeight: '600' }} numberOfLines={2}>
                        {row.name}
                      </Text>
                    </View>
                    {CATEGORIES.map(cat => {
                      const c = row.categories[cat];
                      const color = c.state === 'missing' ? '#F1F5F9'
                                  : c.state === 'expired'  ? DANGER
                                  : c.state === 'expiring' ? WARNING : SUCCESS;
                      return (
                        <View key={cat} style={[st.matrixCell, { width: 70 }]}>
                          <View style={{
                            width: 28, height: 28, borderRadius: 6,
                            backgroundColor: c.state === 'missing' ? '#F1F5F9' : color + '30',
                            alignItems: 'center', justifyContent: 'center',
                          }}>
                            {c.state === 'missing' ? (
                              <Text style={{ color: '#CBD5E1', fontSize: 14 }}>—</Text>
                            ) : (
                              <Text style={{ fontSize: 12, fontWeight: '800', color }}>{c.count}</Text>
                            )}
                          </View>
                        </View>
                      );
                    })}
                  </View>
                ))}
              </View>
            </ScrollView>
            <View style={{ flexDirection: 'row', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
              {[
                { lbl: 'Valid', c: SUCCESS },
                { lbl: 'Expiring', c: WARNING },
                { lbl: 'Expired', c: DANGER },
                { lbl: 'Missing', c: '#CBD5E1' },
              ].map(l => (
                <View key={l.lbl} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <View style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: l.c }} />
                  <Text style={{ fontSize: 11, color: NEUTRAL }}>{l.lbl}</Text>
                </View>
              ))}
            </View>
          </View>
        );

      case 'settings-header':
        return (
          <View style={[st.subHead, { paddingTop: 16 }]}>
            <Text style={st.sectionTitle}>Settings & Exports</Text>
          </View>
        );

      case 'setting-reminder':
        return (
          <View style={st.settingRow}>
            <View style={st.settingIcon}>
              <MaterialCommunityIcons name="bell-outline" size={20} color={PRIMARY} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={st.settingTitle}>Default Reminder Window</Text>
              <Text style={st.settingDesc}>Notify when a cert expires within {settings.defaultReminderDays} days</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 4 }}>
              {[14, 30, 60, 90].map(d => (
                <TouchableOpacity key={d}
                  onPress={() => persistSettings({ ...settings, defaultReminderDays: d })}
                  style={[st.daysChip, settings.defaultReminderDays === d && { backgroundColor: PRIMARY }]}>
                  <Text style={[st.daysChipTxt, settings.defaultReminderDays === d && { color: '#FFF' }]}>{d}d</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        );

      case 'setting-auto':
        return (
          <View style={st.settingRow}>
            <View style={st.settingIcon}>
              <MaterialCommunityIcons name="auto-fix" size={20} color={PRIMARY} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={st.settingTitle}>Auto-Compute Status</Text>
              <Text style={st.settingDesc}>Derive status from expiry date automatically</Text>
            </View>
            <TouchableOpacity
              onPress={() => persistSettings({ ...settings, autoComputeStatus: !settings.autoComputeStatus })}
              style={[{ width: 44, height: 26, borderRadius: 13, padding: 2, justifyContent: 'center' },
                      { backgroundColor: settings.autoComputeStatus ? SUCCESS : '#CBD5E1' }]}>
              <View style={{ width: 22, height: 22, borderRadius: 11, backgroundColor: '#FFF',
                             marginLeft: settings.autoComputeStatus ? 18 : 0 }} />
            </TouchableOpacity>
          </View>
        );

      case 'setting-action':
        return (
          <TouchableOpacity style={st.settingRow} onPress={item.action}>
            <View style={st.settingIcon}>
              <MaterialCommunityIcons name={item.icon} size={20} color={PRIMARY} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={st.settingTitle}>{item.title}</Text>
              <Text style={st.settingDesc}>{item.desc}</Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color="#94A3B8" />
          </TouchableOpacity>
        );

      default:
        return null;
    }
  };

  // ── Modals ────────────────────────────────────────────────────────────────
  const renderFormModal = () => (
    <Modal animationType="slide" transparent visible={showFormModal}
      onRequestClose={() => setShowFormModal(false)}>
      <View style={st.modalOverlay}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={[st.modalBox, { maxHeight: '92%' }]}>
          <View style={st.modalHead}>
            <Text style={st.modalTitle}>{formData.id ? 'Edit Certification' : 'Add Certification'}</Text>
            <TouchableOpacity onPress={() => setShowFormModal(false)}>
              <Ionicons name="close" size={24} color={NEUTRAL} />
            </TouchableOpacity>
          </View>
          <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator>
            <Field label="Certificate Title" value={formData.title}
              onChange={v => updateFormField('title', v)}
              placeholder="e.g. First Aid at Work"
              required error={formErrors.title} />

            <Field label="Certificate Holder" value={formData.holderName}
              onChange={v => updateFormField('holderName', v)}
              placeholder="Employee or person name"
              required error={formErrors.holderName} />

            <Selector label="Issuer" value={formData.issuer}
              options={COMMON_ISSUERS} required
              onChange={v => updateFormField('issuer', v)} />
            {formData.issuer === 'NEBOSH' ? null : (
              <Field label="" value={formData.issuer}
                onChange={v => updateFormField('issuer', v)}
                placeholder="Or type a custom issuer..." />
            )}

            <Field label="Certificate Number" value={formData.certificateNumber}
              onChange={v => updateFormField('certificateNumber', v)}
              placeholder="e.g. SJA-2024-12847" />

            <View style={{ flexDirection: 'row', gap: 8 }}>
              <View style={{ flex: 1 }}>
                <Selector label="Category" value={formData.category}
                  options={CATEGORIES}
                  onChange={v => updateFormField('category', v)} />
              </View>
              <View style={{ flex: 1 }}>
                <Selector label="Level" value={formData.level}
                  options={LEVELS}
                  onChange={v => updateFormField('level', v)} />
              </View>
            </View>

            <View style={{ flexDirection: 'row', gap: 8 }}>
              <View style={{ flex: 1 }}>
                <Field label="Issue Date" value={formData.issueDate}
                  onChange={v => updateFormField('issueDate', v)}
                  placeholder="YYYY-MM-DD" />
              </View>
              <View style={{ flex: 1 }}>
                <Field label="Expiry Date" value={formData.expiryDate}
                  onChange={v => updateFormField('expiryDate', v)}
                  placeholder="YYYY-MM-DD" error={formErrors.expiryDate} />
              </View>
            </View>

            <View style={{ flexDirection: 'row', gap: 8 }}>
              <View style={{ flex: 1 }}>
                <Field label="Cost (£)" value={String(formData.cost || '')}
                  onChange={v => updateFormField('cost', parseFloat(v) || 0)}
                  placeholder="0.00" keyboard="decimal-pad" />
              </View>
              <View style={{ flex: 1 }}>
                <Field label="Reminder (days before expiry)" value={String(formData.reminderDays || '')}
                  onChange={v => updateFormField('reminderDays', parseInt(v) || 0)}
                  placeholder="30" keyboard="numeric" />
              </View>
            </View>

            <View style={st.toggleRow}>
              <View style={{ flex: 1 }}>
                <Text style={st.formLbl}>Renewal Required</Text>
                <Text style={st.settingDesc}>Mark certs that need periodic renewal</Text>
              </View>
              <TouchableOpacity
                onPress={() => updateFormField('renewalRequired', !formData.renewalRequired)}
                style={[{ width: 44, height: 26, borderRadius: 13, padding: 2, justifyContent: 'center' },
                        { backgroundColor: formData.renewalRequired ? SUCCESS : '#CBD5E1' }]}>
                <View style={{ width: 22, height: 22, borderRadius: 11, backgroundColor: '#FFF',
                               marginLeft: formData.renewalRequired ? 18 : 0 }} />
              </TouchableOpacity>
            </View>

            <Field label="Attachment URL" value={formData.attachmentUrl}
              onChange={v => updateFormField('attachmentUrl', v)}
              placeholder="https://... or /uploads/documents/..." />

            <Field label="Notes" value={formData.notes}
              onChange={v => updateFormField('notes', v)}
              placeholder="Any additional notes..."
              multiline />
          </ScrollView>
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 14 }}>
            {formData.id ? (
              <TouchableOpacity style={[st.applyBtn, { flex: 1, backgroundColor: DANGER }]}
                onPress={() => {
                  setShowFormModal(false);
                  deleteCert(formData);
                }}>
                <Text style={{ color: '#FFF', fontWeight: '700' }}>Delete</Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity style={[st.applyBtn, { flex: 2 }]}
              disabled={isSaving} onPress={submitForm}>
              {isSaving ? <ActivityIndicator size={18} color="#FFF" /> :
                <Text style={{ color: '#FFF', fontSize: 15, fontWeight: '700' }}>
                  {formData.id ? 'Update' : 'Add Certification'}
                </Text>}
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  const renderDetailModal = () => {
    if (!selectedCert) return null;
    const c = selectedCert;
    const m = statusMeta(c.status);
    return (
      <Modal animationType="slide" transparent visible={showDetailModal}
        onRequestClose={() => setShowDetailModal(false)}>
        <View style={st.modalOverlay}>
          <View style={[st.modalBox, { maxHeight: '90%' }]}>
            <View style={st.modalHead}>
              <Text style={st.modalTitle} numberOfLines={1}>{c.title}</Text>
              <TouchableOpacity onPress={() => setShowDetailModal(false)}>
                <Ionicons name="close" size={24} color={NEUTRAL} />
              </TouchableOpacity>
            </View>
            <ScrollView>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <View style={[st.certIcon, { backgroundColor: m.bg, width: 56, height: 56 }]}>
                  <MaterialCommunityIcons name="certificate" size={28} color={m.color} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, color: '#0F172A', fontWeight: '700' }}>{c.holderName}</Text>
                  <Text style={{ fontSize: 12, color: NEUTRAL, marginTop: 2 }}>{c.issuer}</Text>
                  <View style={{ flexDirection: 'row', gap: 6, marginTop: 6 }}>
                    <StatusPill status={c.status} />
                    {c.category ? <Text style={st.certMeta}>{c.category}</Text> : null}
                    {c.level ? (
                      <Text style={st.certMeta}>{c.level}</Text>
                    ) : null}
                  </View>
                </View>
              </View>

              {[
                ['Certificate Number', c.certificateNumber],
                ['Issued',             fmtDate(c.issueDate)],
                ['Expires',            fmtDate(c.expiryDate) + (c.expiryDate ? ' · ' + relativeExpiry(c.expiryDate) : '')],
                ['Cost',               c.cost > 0 ? fmtCurrency(c.cost) : '—'],
                ['Renewal',            c.renewalRequired ? `Required (notify ${c.reminderDays}d before)` : 'One-off (no renewal)'],
              ].map(([label, value]) => value ? (
                <View key={label} style={st.detailRow}>
                  <Text style={st.detailLbl}>{label}</Text>
                  <Text style={st.detailVal}>{value}</Text>
                </View>
              ) : null)}

              {c.notes ? (
                <View style={{ marginTop: 10 }}>
                  <Text style={st.detailLbl}>Notes</Text>
                  <Text style={[st.detailVal, { marginTop: 4 }]}>{c.notes}</Text>
                </View>
              ) : null}

              {c.tags?.length > 0 ? (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 12 }}>
                  {c.tags.map((t, i) => (
                    <View key={i} style={{ paddingHorizontal: 8, paddingVertical: 3,
                                            borderRadius: 10, backgroundColor: '#EFF6FF' }}>
                      <Text style={{ fontSize: 10, color: PRIMARY, fontWeight: '700' }}>{t}</Text>
                    </View>
                  ))}
                </View>
              ) : null}

              {c.attachmentUrl ? (
                <TouchableOpacity style={st.attachBox}
                  onPress={() => Linking.openURL(c.attachmentUrl).catch(() =>
                    Alert.alert('Cannot open', 'Could not open attachment URL.'))}>
                  <MaterialCommunityIcons name="paperclip" size={18} color={PRIMARY} />
                  <Text style={{ flex: 1, fontSize: 12, color: PRIMARY, fontWeight: '600' }} numberOfLines={1}>
                    {c.attachmentName || c.attachmentUrl}
                  </Text>
                  <MaterialCommunityIcons name="open-in-new" size={16} color={PRIMARY} />
                </TouchableOpacity>
              ) : null}
            </ScrollView>

            <View style={{ flexDirection: 'row', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <TouchableOpacity style={[st.detailAction, { backgroundColor: PRIMARY }]}
                onPress={() => { setShowDetailModal(false); openEditForm(c); }}>
                <MaterialCommunityIcons name="pencil" size={14} color="#FFF" />
                <Text style={st.detailActionTxt}>Edit</Text>
              </TouchableOpacity>
              {c.renewalRequired ? (
                <TouchableOpacity style={[st.detailAction, { backgroundColor: SUCCESS }]}
                  onPress={() => renewCert(c)}>
                  <MaterialCommunityIcons name="autorenew" size={14} color="#FFF" />
                  <Text style={st.detailActionTxt}>Renew</Text>
                </TouchableOpacity>
              ) : null}
              <TouchableOpacity style={[st.detailAction, { backgroundColor: DANGER }]}
                onPress={() => deleteCert(c)}>
                <MaterialCommunityIcons name="delete" size={14} color="#FFF" />
                <Text style={st.detailActionTxt}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const renderFilterModal = () => (
    <Modal animationType="slide" transparent visible={showFilterModal}
      onRequestClose={() => setShowFilterModal(false)}>
      <View style={st.modalOverlay}>
        <View style={st.modalBox}>
          <View style={st.modalHead}>
            <Text style={st.modalTitle}>Filter Certifications</Text>
            <TouchableOpacity onPress={() => setShowFilterModal(false)}>
              <Ionicons name="close" size={24} color={NEUTRAL} />
            </TouchableOpacity>
          </View>
          <ScrollView style={{ maxHeight: 480 }}>
            {[
              { key: 'status',   title: 'Status',
                opts: [['all', 'All'], ['valid', 'Valid'], ['expiring', 'Expiring'], ['expired', 'Expired']] },
              { key: 'category', title: 'Category',
                opts: [['all', 'All'], ...CATEGORIES.map(c => [c, c])] },
              { key: 'issuer',   title: 'Issuer',
                opts: [['all', 'All'], ...Array.from(new Set(certs.map(c => c.issuer).filter(Boolean))).map(i => [i, i])] },
              { key: 'employee', title: 'Holder',
                opts: [['all', 'All'], ...Array.from(new Set(certs.map(c => c.holderName).filter(Boolean))).map(n => [n, n])] },
              { key: 'renewal',  title: 'Renewal',
                opts: [['all', 'All'], ['required', 'Required'], ['one_off', 'One-off']] },
            ].map(sec => (
              <View key={sec.key} style={{ marginBottom: 16 }}>
                <Text style={st.filterSecTitle}>{sec.title}</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {sec.opts.map(([val, lbl]) => (
                    <Chip key={val} label={lbl}
                      active={filterOptions[sec.key] === val}
                      onPress={() => setFilterOptions(prev => ({ ...prev, [sec.key]: val }))} />
                  ))}
                </View>
              </View>
            ))}
          </ScrollView>
          <TouchableOpacity style={st.applyBtn} onPress={() => setShowFilterModal(false)}>
            <Text style={{ color: '#FFF', fontSize: 15, fontWeight: '700' }}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <SafeAreaView style={st.screen}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color={PRIMARY} />
          <Text style={{ marginTop: 12, color: NEUTRAL, fontWeight: '500' }}>
            Loading certifications...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={st.screen}>
      <StatusBar barStyle="light-content" backgroundColor={PRIMARY} />
      <LinearGradient colors={[PRIMARY, PRIMARY_2]} style={st.header}>
        <View style={st.headerRow}>
          <TouchableOpacity onPress={() => navigation?.goBack?.()} style={st.hBtn}>
            <Ionicons name="arrow-back" size={22} color="#FFF" />
          </TouchableOpacity>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text style={st.hTitle}>Certifications</Text>
            <Text style={st.hSub}>{companyName || `Company #${companyId}`}</Text>
          </View>
          <TouchableOpacity onPress={() => setShowFilterModal(true)} style={st.hBtn}>
            <MaterialCommunityIcons name="filter-variant" size={20} color="#FFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {renderTabBar()}

      <FlatList
        data={flatListData}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]} />}
        contentContainerStyle={{ paddingBottom: 110 }}
        removeClippedSubviews
        windowSize={11}
        initialNumToRender={6}
      />

      {/* FAB */}
      <View style={st.fabContainer} pointerEvents="box-none">
        {fabOpen ? (
          <Animated.View style={[st.fabMenu, {
            opacity: fabAnim,
            transform: [{ scale: fabAnim }, {
              translateY: fabAnim.interpolate({ inputRange: [0, 1], outputRange: [20, 0] }),
            }],
          }]} pointerEvents="auto">
            {[
              { icon: 'plus',     label: 'Add Certification', color: PRIMARY,
                onPress: () => { toggleFAB(); openAddForm(); }},
              { icon: 'download', label: 'Export CSV',        color: SUCCESS,
                onPress: () => { toggleFAB(); exportCSV(); }},
              { icon: 'file-document-outline', label: 'Compliance Report', color: INFO,
                onPress: () => { toggleFAB(); exportComplianceReport(); }},
              { icon: 'select-multiple', label: 'Bulk Select', color: PURPLE,
                onPress: () => { toggleFAB(); setSelectMode(true); }},
            ].map(it => (
              <TouchableOpacity key={it.label} style={st.fabItem} onPress={it.onPress}>
                <Text style={st.fabItemLbl}>{it.label}</Text>
                <View style={[st.fabItemBtn, { backgroundColor: it.color }]}>
                  <MaterialCommunityIcons name={it.icon} size={18} color="#FFF" />
                </View>
              </TouchableOpacity>
            ))}
          </Animated.View>
        ) : null}
        <TouchableOpacity style={[st.fab, { backgroundColor: fabOpen ? DANGER : PRIMARY }]}
          onPress={toggleFAB} activeOpacity={0.85}>
          <MaterialCommunityIcons name={fabOpen ? 'close' : 'plus'} size={26} color="#FFF" />
        </TouchableOpacity>
      </View>

      {renderFormModal()}
      {renderDetailModal()}
      {renderFilterModal()}
    </SafeAreaView>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// STYLES
// ════════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  screen: { flex: 1, backgroundColor: BG },

  header: { paddingHorizontal: 16, paddingVertical: 12,
    paddingTop: Platform.OS === 'ios' ? 50 : 14 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn: { width: 36, height: 36, borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.15)', alignItems: 'center', justifyContent: 'center' },
  hTitle: { fontSize: 18, fontWeight: '800', color: '#FFF' },
  hSub:   { fontSize: 11, color: '#BFDBFE', marginTop: 1 },

  tabBar: { backgroundColor: SURFACE, borderBottomWidth: 1, borderBottomColor: BORDER },
  tabBtn: { flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 12, paddingVertical: 10, borderRadius: 14, marginRight: 2 },
  tabBtnActive: { backgroundColor: '#EFF6FF' },
  tabTxt: { fontSize: 12, color: '#94A3B8', fontWeight: '500' },

  pulseCard: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    backgroundColor: SURFACE, margin: 16, marginBottom: 8, padding: 16, borderRadius: 12,
    borderWidth: 1, borderColor: BORDER },
  pulseLbl:  { fontSize: 10, color: NEUTRAL, fontWeight: '700' },
  pulseVal:  { fontSize: 36, fontWeight: '800', marginTop: 2 },
  pulseDesc: { fontSize: 11, color: NEUTRAL, marginTop: 4, maxWidth: 220 },

  statsGrid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: 16,
    marginBottom: 8, gap: 8 },
  statCard: { width: (width - 48) / 3, backgroundColor: SURFACE, padding: 10,
    borderRadius: 12, borderWidth: 1, borderColor: BORDER, alignItems: 'center' },
  statVal: { fontSize: 16, fontWeight: '800', marginTop: 4 },
  statLbl: { fontSize: 10, color: NEUTRAL, marginTop: 2, fontWeight: '600' },

  sectionCard: { backgroundColor: SURFACE, marginHorizontal: 16, marginBottom: 8,
    padding: 14, borderRadius: 12, borderWidth: 1, borderColor: BORDER },
  sectionTitle: { fontSize: 15, fontWeight: '800', color: '#0F172A' },
  sectionSub: { fontSize: 11, color: NEUTRAL, marginTop: 2 },
  subHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 16, paddingBottom: 4, paddingTop: 6 },

  statusBox: { flex: 1, alignItems: 'center', padding: 10, borderRadius: 10,
    backgroundColor: '#F8FAFC', borderLeftWidth: 3 },
  statusBoxNum: { fontSize: 22, fontWeight: '800' },
  statusBoxLbl: { fontSize: 11, color: NEUTRAL, fontWeight: '600' },

  searchRow: { flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: SURFACE, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8,
    borderWidth: 1, borderColor: BORDER },
  searchInput: { flex: 1, fontSize: 14, color: '#0F172A', paddingVertical: 0 },

  certCard: { backgroundColor: SURFACE, marginHorizontal: 16, marginBottom: 8,
    padding: 14, borderRadius: 12, borderWidth: 1, borderColor: BORDER },
  certIcon: { width: 40, height: 40, borderRadius: 10,
    alignItems: 'center', justifyContent: 'center' },
  certTitle: { fontSize: 14, fontWeight: '800', color: '#0F172A' },
  certHolder: { fontSize: 12, color: NEUTRAL, marginTop: 2, fontWeight: '500' },
  certMeta: { fontSize: 11, color: NEUTRAL, fontWeight: '600' },
  certMetaDot: { fontSize: 11, color: NEUTRAL },
  certExpiry: { fontSize: 11, marginTop: 4, fontWeight: '700' },
  certCost: { fontSize: 13, color: SUCCESS, fontWeight: '800' },

  checkbox: { width: 22, height: 22, borderRadius: 6,
    borderWidth: 2, borderColor: '#CBD5E1', alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#FFF' },

  emptyBox: { alignItems: 'center', padding: 36, marginHorizontal: 16 },
  emptyTxt: { fontSize: 14, color: NEUTRAL, marginTop: 10, fontWeight: '500', textAlign: 'center' },
  addBtn: { flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: PRIMARY, paddingHorizontal: 14, paddingVertical: 10,
    borderRadius: 10, marginTop: 14 },

  bulkBtn: { flexDirection: 'row', alignItems: 'center', gap: 3,
    backgroundColor: '#FEE2E2', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 },
  clearBtn: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, backgroundColor: '#FEE2E2' },

  matrixCell: { padding: 8, alignItems: 'center', justifyContent: 'center',
    borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#F1F5F9' },
  matrixHeaderCell: { backgroundColor: '#F8FAFC' },
  matrixHeaderTxt: { fontSize: 11, fontWeight: '800', color: '#475569', textTransform: 'uppercase' },

  settingRow: { flexDirection: 'row', alignItems: 'center',
    backgroundColor: SURFACE, marginHorizontal: 16, marginBottom: 6,
    padding: 12, borderRadius: 12, borderWidth: 1, borderColor: BORDER, gap: 10 },
  settingIcon: { width: 36, height: 36, borderRadius: 10,
    backgroundColor: '#EFF6FF', alignItems: 'center', justifyContent: 'center' },
  settingTitle: { fontSize: 14, fontWeight: '700', color: '#0F172A' },
  settingDesc: { fontSize: 11, color: NEUTRAL, marginTop: 2 },
  daysChip: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8,
    backgroundColor: '#F1F5F9', borderWidth: 1, borderColor: BORDER },
  daysChipTxt: { fontSize: 11, color: NEUTRAL, fontWeight: '700' },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modalBox: { backgroundColor: '#FFF', borderTopLeftRadius: 20, borderTopRightRadius: 20,
    padding: 20, maxHeight: '92%' },
  modalHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 12 },
  modalTitle: { fontSize: 17, fontWeight: '800', color: '#0F172A' },

  formLbl: { fontSize: 13, fontWeight: '600', color: '#334155', marginBottom: 6 },
  formInput: { backgroundColor: '#F8FAFC', borderWidth: 1.5, borderColor: BORDER,
    borderRadius: 10, paddingHorizontal: 14, paddingVertical: 10, fontSize: 14, color: '#0F172A' },
  formErr: { fontSize: 11, color: DANGER, marginTop: 4 },

  selectorDropdown: { backgroundColor: '#FFF', borderRadius: 10, marginTop: 4,
    borderWidth: 1, borderColor: BORDER, overflow: 'hidden' },
  selectorOpt: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: 10, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },

  toggleRow: { flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F1F5F9', marginBottom: 12 },

  filterSecTitle: { fontSize: 12, fontWeight: '700', color: '#475569',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
  applyBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: PRIMARY, borderRadius: 12, paddingVertical: 14, gap: 6 },

  detailRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start',
    paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  detailLbl: { fontSize: 11, color: NEUTRAL, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },
  detailVal: { fontSize: 13, color: '#0F172A', fontWeight: '600', maxWidth: '60%', textAlign: 'right' },
  detailAction: { flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8 },
  detailActionTxt: { color: '#FFF', fontSize: 12, fontWeight: '700' },
  attachBox: { flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#EFF6FF', padding: 12, borderRadius: 8, marginTop: 12 },

  fabContainer: { position: 'absolute', bottom: 20, right: 20, alignItems: 'flex-end' },
  fab: { width: 56, height: 56, borderRadius: 28, alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 6,
    elevation: 8 },
  fabMenu: { alignItems: 'flex-end', marginBottom: 10, gap: 10 },
  fabItem: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  fabItemLbl: { backgroundColor: SURFACE, paddingHorizontal: 10, paddingVertical: 5,
    borderRadius: 8, fontSize: 12, fontWeight: '600', color: '#1E293B',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.15,
    shadowRadius: 3, elevation: 3 },
  fabItemBtn: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2,
    shadowRadius: 4, elevation: 5 },
});
